import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PAGE_SIZE } from '@/constants';
import { PRIVATE_PAGE_ROUTES } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { AssetGridPicker } from '@/pages/DigitalSignage/DeviceManagement/Tab/components/AssetGridPicker';
import { useGetOrganizationAssets, useGetTextOverlayByAssetIdMutation } from '@/services';
import { Asset } from '@/types';

interface OrganizationAssetsPickerProps {
  selectedAssetId: number | null;
  onAssetSelect: (asset: Asset) => void;
  assetTypes: string[];
  deviceScheduleId: number;
}

export const OrganizationAssetsPicker = ({
  selectedAssetId,
  onAssetSelect,
  assetTypes = [],
  deviceScheduleId
}: OrganizationAssetsPickerProps) => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();

  const { mutate: getTextOverlayByAssetId } = useGetTextOverlayByAssetIdMutation({
    onSuccess: (response) => {
      const textOverlayId = response?.data?.id;
      const organizationId = response?.data?.asset?.organizationId;

      if (textOverlayId && organizationId) {
        navigate(
          `${PRIVATE_PAGE_ROUTES.EDIT_TEXT_OVERLAY_FOR_ORGANIZATION.replace(
            ':organizationId',
            String(organizationId)
          ).replace(
            ':textOverlayId',
            String(textOverlayId)
          )}?returnTo=digital-signage&deviceScheduleId=${deviceScheduleId}`
        );
      }
    },
    onError: () => {
      showToast(ToastType.ERROR, t('somethingWentWrong'));
    }
  });

  const {
    data: organizationAssetsData,
    isLoading: isLoadingOrganizationAssets,
    fetchNextPage,
    hasNextPage
  } = useGetOrganizationAssets(PAGE_SIZE, true, undefined, '', assetTypes, 'CreatedAt', false);

  const organizationAssets =
    organizationAssetsData?.pages.flatMap((page) => page?.data?.items as Asset[]) || [];

  return (
    <AssetGridPicker
      assets={organizationAssets}
      selectedAssetId={selectedAssetId}
      onAssetSelect={onAssetSelect}
      onAssetEdit={(asset) => getTextOverlayByAssetId(asset.id)}
      isLoading={isLoadingOrganizationAssets}
      emptyLabel={t('noAssetsFound')}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isCardEditable={true}
    />
  );
};
